import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { useNavigate, useParams } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";
import {
  CheckCircleIcon,
  ClockIcon,
  KeyIcon,
  LockClosedIcon,
  LockOpenIcon,
  Squares2X2Icon,
  TableCellsIcon,
  UserCircleIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

import PartDiagramModal, { DiagramMode } from "../../components/GtMotiveWidgets/PartDiagramModal";
import Depollution from "../Depollution/Depollution";
import DismantlingTimerCard from "../../components/jobs/DismantlingTimerCard";
import JobPartsByAssembly from "../../components/jobs/JobPartsByAssembly";
import useNotification from "../../components/notifications/useNotifications";
import Breadcrumbs from "../../components/shared/Breadcrumbs/Breadcrumbs";
import Card from "../../components/shared/Card/Card";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import ReasonedModal from "../../components/shared/Confirm/ReasonedModal";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import NotesOutput from "../../components/shared/Notes/NotesOutput";
import PinProtect, { PinProtectState } from "../../components/shared/PinProtect";
import VehicleHeader from "../../components/vehicles/VehicleHeader";
import VehicleLocationDropdown from "../../components/vehicles/VehicleLocationDropdown";
import VehicleWorkflowCard from "../../components/vehicles/VehicleWorkflowCard";
import { useAuth } from "../../contexts/AuthContext";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCustomerDetails from "../../data/useCustomerDetails";
import useNotes from "../../data/useNotes";
import usePartMutations from "../../data/usePartMutations";
import usePartsLeadLookup from "../../data/usePartsLeadLookup";
import useSingleJob from "../../data/useSingleJob";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import { useUserLookup } from "../../hooks/useUserLookup";
import { IJob, JobPartCategory } from "../../models/Job";
import { INewNote, ResourceType } from "../../models/Note";
import { IPart, IPartCategory, PartStatus } from "../../models/Part";
import { PartProBlackList } from "../../models/PartLookup";
import classNames from "../../utils/classNames";
import { resetTimer as resetDismantlingTimer } from "../../utils/dismantlingTimers";
import formatDateTime from "../../utils/formatDateTime";
import { isJobLocked, setJobLocked, useLockedJobs } from "../../utils/jobLocks";

export default function ViewJob() {
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();

  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Job");
  const { saveHandlers: partSaveHandlers } = useDefaultCRUDHandlers("Part");
  const { userId } = useAuth();
  const { jobId } = useParams();
  const { create, remove } = useNotes();
  const [showDeleted, setShowDeleted] = useState(false);
  const { singleJob, update, remove: removeJob } = useSingleJob(jobId!, showDeleted);
  const { markDamaged, patchPart } = usePartMutations();
  const { generatePartLabel } = useLabelGenerator();
  const { mechanics } = useUserLookup();
  // Subscribe to the job-lock store so the Lock/Unlock button and "Locked"
  // chip re-render in sync when the state is toggled elsewhere (e.g. via the
  // Awaiting Dismantling right-click menu). We read via `isJobLocked` below
  // rather than the returned helper, but calling the hook is what registers
  // the re-render listener.
  useLockedJobs();

  const [job, setJob] = useState<IJob | undefined>();
  const [completeOpen, setCompleteOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [damagedOpen, setDamagedOpen] = useState(false);
  const [damaged, setDamaged] = useState<IPart | null>();
  const [isCompleteLoading, setIsCompleteLoading] = useState(false);
  const [isDamagedLoading, setIsDamagedLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);


  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinState, setPinState] = useState<PinProtectState>();
  const [selectedPartIds, setSelectedPartIds] = useState<Set<string>>(new Set());
  const [jobPartsView, setJobPartsView] = useState<"tiles" | "table">("tiles");

  const [diagramOpen, setDiagramOpen] = useState(false);
  const [diagramCategory, setDiagramCategory] = useState<IPartCategory | null>(null);
  const [diagramMode, setDiagramMode] = useState<DiagramMode>({ type: "findAll" });
  const [diagramPartDataId, setDiagramPartDataId] = useState<string | null>(null);
  /** Browse-only vehicle diagram from Dismantling Summary — hides Save in the modal. */
  const [diagramHideSave, setDiagramHideSave] = useState(false);

  // PartPro lookup — scoped to the job's lead/vehicle so the Parts card can
  // refresh PartPro prices/matches without having to jump back to CreateJobs.
  const [partProOpen, setPartProOpen] = useState(false);
  const [partProLoading, setPartProLoading] = useState(false);
  const { brand } = useCustomerDetails();
  const { lookupMutation: partProLookupMutation } = usePartsLeadLookup(
    job?.lead?.id,
    job?.lead?.vehicle?.id ?? job?.lead?.vehicleId ?? "",
    job?.lead?.partsLookupRequestId ??
      job?.lead?.vehicle?.partsLookupRequestId,
    "ViewJob"
  );

  const partProHasLookup = !!(
    job?.lead?.partsLookupRequestId ?? job?.lead?.vehicle?.partsLookupRequestId
  );
  const partProBlacklisted = useMemo(() => {
    const make = (job?.lead?.vehicle?.combinedMake ?? "").toLowerCase();
    return PartProBlackList.includes(make);
  }, [job?.lead?.vehicle?.combinedMake]);
  const partProUnavailable = !job?.lead?.id && !job?.lead?.vehicle?.id;

  const handlePartProLookup = () => {
    setPartProLoading(true);
    partProLookupMutation.mutate(partProHasLookup, {
      onSuccess: () => {
        addNotification({
          primaryText: "PartPro Lookup Successful",
          variant: "success",
        });
        // Refresh the job so the parts table picks up any newly-matched
        // UCIDs / PartPro prices that the lookup wrote against each part.
        queryClient.refetchQueries(["job", jobId]);
        setPartProLoading(false);
        setPartProOpen(false);
      },
      onError: (error) => {
        console.error("PartPro lookup failed:", error);
        addNotification({
          primaryText: "PartPro Lookup Failed",
          variant: "error",
        });
        setPartProLoading(false);
        setPartProOpen(false);
      },
    });
  };


  useEffect(() => {
    if (!singleJob.isLoading) {
      setJob(singleJob.data?.data);

      setInfo({
        name: `${singleJob.data?.data.title} Job`,
        desc: `View and manage ${singleJob.data?.data.title}'s Job`,
      });

      setIsLoading(false);
    }
  }, [singleJob.isLoading, singleJob.data, jobId])

  const handleComplete = () => {
    setIsCompleteLoading(true);

    if (!userId || !job) {
      saveHandlers.onError();
      return;
    }

    const updatedJob = {
      ...job!,
      completedOn: new Date().toISOString(),
      completedBy: userId,
    }

    update.mutate(updatedJob, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setCompleteOpen(false);
      },
      onError: saveHandlers.onError,
      onSettled: () => setIsCompleteLoading(false),
    });

  }

  const handleReset = () => {
    if (!userId || !job) {
      saveHandlers.onError();
      return;
    }

    const updatedJob: IJob = {
      ...job!,
      startDate: null,
      endDate: null,
      mechanicId: null,
      completedBy: null,
      completedOn: null,
    }

    // Clear the client-side dismantling timer too — otherwise a reset job
    // would keep its old stopwatch state and confuse the next mechanic.
    resetDismantlingTimer(job.id);

    update.mutate(updatedJob, saveHandlers);
  }

  // Dismantling-timer → job sync: the first Start back-fills `startDate`
  // (if the planner hasn't set one), Stop fills `endDate`, and Reset from
  // the timer clears both so the job drops back into "awaiting dismantling"
  // territory. These go through the same `update` mutation the Reset and
  // Complete buttons use, so all three stay consistent.
  const handleTimerFirstStart = (startedAt: string) => {
    if (!userId || !job) return;
    if (job.startDate) return;
    const updatedJob: IJob = {
      ...job,
      startDate: startedAt,
      mechanicId: job.mechanicId ?? userId,
    };
    update.mutate(updatedJob, saveHandlers);
  };

  const handleTimerStop = (stoppedAt: string) => {
    if (!userId || !job) return;
    const updatedJob: IJob = {
      ...job,
      startDate: job.startDate ?? stoppedAt,
      endDate: stoppedAt,
    };
    update.mutate(updatedJob, saveHandlers);
  };

  const handleTimerReset = () => {
    if (!userId || !job) return;
    // Mirror the Reset Job button behaviour so the two actions never drift:
    // clears schedule, mechanic and completion metadata on the job record.
    const updatedJob: IJob = {
      ...job,
      startDate: null,
      endDate: null,
      completedBy: null,
      completedOn: null,
    };
    update.mutate(updatedJob, saveHandlers);
  };

  const handleReopen = () => {
    if (!userId || !job) {
      saveHandlers.onError();
      return;
    }

    const updatedJob: IJob = {
      ...job!,
      completedOn: null,
      completedBy: null,
    }

    update.mutate(updatedJob, saveHandlers);
  }

  const handlePrint = async (part: IPart | null) => {
    if (!part) return;
    part.vehicle = job?.lead?.vehicle ?? null;

    const html = await generatePartLabel(part);
    var iframe = document?.getElementById("frame") as HTMLIFrameElement;
    var pri = iframe.contentWindow;
    pri?.document.open();
    pri?.document.write(html);
    pri?.document.close();
    pri?.focus();

    //QR code doesn't show up without a timeout
    setTimeout(async () => {
      pri?.print();
    }, 1);
  };

  const handleMultiPrint = async () => {
    var parts = new Array();

    job?.jobPartCategories.forEach(jobPartCategories => {
      parts.push(jobPartCategories.partData);
    });

    await printParts(parts);
  };

  // This will exclude any parts that are not yet removed, or are damanged
  const handleRemovedMultiPrint = async () => {
    var parts = new Array();

    job?.jobPartCategories.forEach(jobPartCategories => {
      if (jobPartCategories.partData?.partStatus === PartStatus.Dismantled) parts.push(jobPartCategories.partData);
    });

    if (parts.length === 0) {
      console.error("No removed parts to print labels for.");
      return;
    }

    await printParts(parts);
  }

  const handleSelectedPartsPrint = async () => {
    if (selectedPartIds.size === 0) {
      addNotification({
        variant: "warning",
        primaryText: "No Parts Selected",
      });
      return;
    }

    const parts = job?.jobPartCategories
      .filter(jobPart => jobPart.partData?.id && selectedPartIds.has(jobPart.partData.id))
      .map(jobPart => jobPart.partData)
      .filter((part): part is IPart => part !== null) || [];

    if (parts.length === 0) {
      addNotification({
        variant: "warning",
        primaryText: "No Valid Parts Selected",
      });
      return;
    }

    await printParts(parts);
  }

  const handleMarkSelectedAsRemoved = () => {
    if (selectedPartIds.size === 0) {
      addNotification({
        variant: "warning",
        primaryText: "No Parts Selected",
      });
      return;
    }

    const selectedParts = job?.jobPartCategories
      .filter(jobPart => jobPart.partData?.id && selectedPartIds.has(jobPart.partData.id))
      .map(jobPart => jobPart.partData)
      .filter((part): part is IPart => part !== null && !part.removedOn) || [];

    if (selectedParts.length === 0) {
      addNotification({
        variant: "warning",
        primaryText: "No Valid Parts Selected",
      });
      return;
    }

    // Set up pin modal to mark all selected parts as removed with a single pin entry
    setPinModalOpen(true);
    setPinState({
      onPinSuccess: (pinUserId) => {
        // Mark all selected parts as removed
        selectedParts.forEach(part => {
          const updatedPart = {
            id: part.id,
            removedOn: new Date().toISOString(),
            removedBy: pinUserId,
            partStatus: PartStatus.Dismantled,
          };

          patchPart.mutate(updatedPart, partSaveHandlers);
        });

        // Clear selection after marking
        setSelectedPartIds(new Set());
      },
      onPinFailed: () => {
      },
      pinLabel: `Mark ${selectedParts.length} Selected Part${selectedParts.length > 1 ? 's' : ''} as Removed`,
    });
  }

  const togglePartSelection = (partId: string | null | undefined) => {
    if (!partId) return;

    setSelectedPartIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(partId)) {
        newSet.delete(partId);
      } else {
        newSet.add(partId);
      }
      return newSet;
    });
  }

  const handleDiagramClick = (jobPart: JobPartCategory) => {
    const cat = jobPart.partCategory;
    if (!cat) return;

    // The diagram modal can only render with a vehicle UMC. Rather than
    // hiding the button when UMC is missing (which operators find
    // confusing — "the button disappeared?"), keep it visible on every
    // part and show a notification explaining why nothing happened.
    if (!job?.lead?.vehicle?.umc) {
      addNotification({
        variant: "error",
        primaryText: "No parts diagram available",
        secondaryText:
          "This vehicle doesn't have a UMC on file yet, so a parts diagram can't be loaded. Add the UMC to the vehicle to unlock diagram search.",
      });
      return;
    }

    const matchedUcid = jobPart.partData?.matchedUcid;

    setDiagramHideSave(false);
    setDiagramCategory(cat);
    setDiagramPartDataId(jobPart.partData?.id ?? null);
    if (cat.ucidZone && matchedUcid) {
      setDiagramMode({ type: "view", zoneId: cat.ucidZone, ucid: matchedUcid });
    } else if (cat.ucidZone) {
      setDiagramMode({ type: "findZone", zoneId: cat.ucidZone });
    } else {
      setDiagramMode({ type: "findAll" });
    }
    setDiagramOpen(true);
  };

  const handleOpenVehiclePartsDiagram = () => {
    const umc = job?.lead?.vehicle?.umc;
    if (!umc || !job) return;
    const row = job.jobPartCategories?.find((j) => j.partCategory);
    const cat = row?.partCategory;
    if (!cat) return;

    setDiagramHideSave(true);
    setDiagramCategory({
      ...cat,
      name: `${job.lead?.vehicle?.vrm ?? "Vehicle"} — parts diagram`,
    });
    setDiagramPartDataId(null);
    setDiagramMode({ type: "findAll" });
    setDiagramOpen(true);
  };

  const toggleAssemblySelection = (assemblyParts: JobPartCategory[]) => {
    const partIds = assemblyParts
      .map(jobPart => jobPart.partData?.id)
      .filter((id): id is string => id !== null && id !== undefined);

    if (partIds.length === 0) return;

    // Check if all parts in assembly are selected
    const allSelected = partIds.every(id => selectedPartIds.has(id));

    setSelectedPartIds(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselect all parts in assembly
        partIds.forEach(id => newSet.delete(id));
      } else {
        // Select all parts in assembly
        partIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  }

  const printParts = async (parts: any[]) => {
    let htmlPages = [] as string[];

    // Use Promise.all to wait for all promises to resolve
    await Promise.all(parts.map(async (part) => {
      const html = await generatePartLabel(part);
      htmlPages.push(html);
    }));

    //Stitch together with a page break inbetween each
    const allHtml = htmlPages.join("<div style='page-break-after: always;'></div>");

    var iframe = document?.getElementById("frame") as HTMLIFrameElement;
    var pri = iframe.contentWindow;
    pri?.document.open();
    pri?.document.write(allHtml);
    pri?.document.close();
    pri?.focus();

    //QR code doesn't show up without a timeout
    setTimeout(async () => {
      pri?.print();
    }, 1);
  }

  const handleDamaged = (message: string) => {
    setPinModalOpen(true);
    setPinState({
      onPinSuccess: (pinUserId) => {
        let body = {
          id: damaged?.id,
          isDamaged: !damaged?.isDamaged,
          partStatus: PartStatus.Damaged,
          // damageReportedBy: pinUserId,
        };

        setIsDamagedLoading(true);

        markDamaged.mutate(body, {
          onSuccess: () => {
            setDamagedOpen(false);
            saveHandlers.onSuccess();
          },
          onError: () => {
            saveHandlers.onError();
          },
          onSettled: () => {
            setIsDamagedLoading(false);
          }
        })

        const newNote: INewNote = {
          message: `Damage Reported For ${damaged?.title} - ${damaged?.tagNumber}\n${message}`,
          resourceId: job?.id!,
          resourceType: ResourceType.Job,
          date: new Date().toISOString(),
        };

        create.mutate(newNote, {
          onSuccess: () => {
            saveHandlers.onSuccess();
            setTimeout(() => {
              queryClient.refetchQueries(["job", jobId]);
            }, 200);
          },
          onError: saveHandlers.onError
        });
      },
      onPinFailed: () => {
      },
      pinLabel: "Mark Part Damaged",
    });
  };

  const markAsRemoved = (part: IPart | null) => {
    if (!userId || !part) {
      partSaveHandlers.onError();
      return;
    }

    setPinModalOpen(true);
    setPinState({
      onPinSuccess: (pinUserId) => {
        const updatedPart = {
          id: part.id,
          removedOn: new Date().toISOString(),
          removedBy: pinUserId,
          partStatus: PartStatus.Dismantled,
        };

        patchPart.mutate(updatedPart, partSaveHandlers);
      },
      onPinFailed: () => {
      },
      pinLabel: "Mark Part Removed",
    });

  };

  const handleDeleteJob = () => {
    removeJob.mutate(jobId!, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setDeleteOpen(false);
        //go back to jobs
        navigate("/jobs");
      },
      onError: deleteHandlers.onError,
    });
  };

  const pages = [
    { to: "..", label: "Jobs", current: false },
    {
      to: jobId && job ? `/jobs/${jobId}` : "",
      label: jobId && job ? `Job for ${job.title.replace(/-[^-]*$/, "")}` : "New Lead",
      current: true,
    },
  ];

  // Job-summary derived state — totals + per-status splits for the
  // "parts to be removed" panel. Split on `partStatus` so the card reads
  // the same way the parts table does (Dismantled / Damaged / Remaining).
  const jobSummary = useMemo(() => {
    const cats = job?.jobPartCategories ?? [];
    const total = cats.length;
    let removed = 0;
    let damaged = 0;
    for (const cat of cats) {
      const part = cat.partData as (IPart & { deleted?: string | null }) | null;
      if (!part) continue;
      if (part.deleted) continue; // soft-deleted shouldn't inflate counts
      if (part.partStatus === PartStatus.Dismantled || part.removedOn) {
        removed += 1;
      } else if (
        part.partStatus === PartStatus.Damaged ||
        (part as IPart).isDamaged
      ) {
        damaged += 1;
      }
    }
    const remaining = Math.max(0, total - removed - damaged);
    const progress = total > 0 ? Math.round((removed / total) * 100) : 0;
    return { total, removed, damaged, remaining, progress };
  }, [job?.jobPartCategories]);

  // Estimated dismantling time is stored on the job in minutes; format as
  // "Xh Ym" once we go over an hour so supervisors don't have to mentally
  // divide by 60.
  const formattedDuration = useMemo(() => {
    const mins = job?.duration ?? 0;
    if (!mins) return "—";
    if (mins < 60) return `${mins} mins`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  }, [job?.duration]);

  const assignedMechanic =
    (job?.mechanicId &&
      mechanics?.find((m) => m.id === job.mechanicId)?.fullname) ||
    job?.mechanic?.fullname ||
    null;

  const locked = job ? isJobLocked(job.id) : false;
  const completed = !!job?.completedOn;

  if (isLoading) {
    return <LoadingWheel />;
  }

  return (
    <>
      {diagramCategory && job?.lead?.vehicle?.umc && (
        <PartDiagramModal
          open={diagramOpen}
          setOpen={setDiagramOpen}
          umc={job.lead.vehicle.umc}
          category={diagramCategory}
          mode={diagramMode}
          partDataId={diagramPartDataId}
          hideSave={diagramHideSave}
        />
      )}
      <PinProtect open={pinModalOpen} setOpen={setPinModalOpen} pinState={pinState} useAuthUser={false} />
      <ReasonedModal open={damagedOpen} setOpen={setDamagedOpen} onConfirm={handleDamaged} isLoading={isDamagedLoading}
        title={"Mark Part as Damaged?"}
        message={`Are you sure you want to mark this part as damaged? \nThis will remove ${damaged?.title} from the job and not allow it to be listed.`}
        confirmButtonText={"Confirm"}
      />
      <ConfirmModal open={completeOpen} setOpen={setCompleteOpen} onConfirm={handleComplete} isLoading={isCompleteLoading}
        title={"Complete Job"} message={"Are you sure you want to mark this job as completed?"} confirmButtonText={"Complete"}
      />
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen} onConfirm={() => handleDeleteJob()} isLoading={false}
        title={"Delete Job?"}
        message={`Are you sure you want to delete this job? \nThis action cannot be undone and parts will be left in their current states.`}
        confirmButtonText={"Delete"}
      />
      <ConfirmModal
        open={partProOpen}
        setOpen={setPartProOpen}
        onConfirm={handlePartProLookup}
        isLoading={partProLoading}
        title="Activate PartPro Lookup?"
        message={`This will start a PartPro lookup on this vehicle, this may take some time.\n${
          partProHasLookup
            ? "This lookup has already been completed for this vehicle, but you can run it again if you want to refresh the data."
            : ""
        }\nIt checks each part against an exact part-number match and updates the parts table with PartPro pricing and matched UCIDs.\n
          This costs 1 token per lookup per vehicle (multiple parts from the same vehicle count as one lookup), you have ${
            brand.data?.data.lookupTokens ?? 0
          } tokens remaining.`}
        confirmButtonText={null}
      />
      <Breadcrumbs pages={pages} />

      <div className="mt-4 space-y-4">
        {/* Vehicle hero — same component the redesigned Vehicle page uses.
            Fed from `job.lead.vehicle`, so mechanics see the donor car they're
            dismantling (image, VRM, stock number, make/model/year/colour) in
            a layout they'll recognise from other Wilma pages. */}
        {job?.lead?.vehicle && (
          <VehicleHeader
            vehicle={job.lead.vehicle}
            lead={job.lead}
            locationControls={
              <>
                <div className="flex flex-col rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5 shadow-sm">
                  <p className="text-xs font-medium text-gray-500">
                    Yard Location
                  </p>
                  <VehicleLocationDropdown
                    vehicle={job.lead.vehicle}
                    classes="mt-1 w-full rounded-md border-slate-300 bg-white py-1.5 pl-3 pr-8 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                <div className="flex flex-col justify-end rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5 shadow-sm">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={showDeleted}
                      onChange={(e) => setShowDeleted(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="font-medium">Show deleted</span>
                  </label>
                </div>
              </>
            }
            headerActions={
              <>
                <ButtonInput
                  label="Print All Labels"
                  isSubmit={false}
                  onClick={() => handleMultiPrint()}
                  classes=""
                />
                <ButtonInput
                  label="Print Removed Labels"
                  isSubmit={false}
                  onClick={() => handleRemovedMultiPrint()}
                  classes=""
                />
                {selectedPartIds.size > 0 && (
                  <>
                    <ButtonInput
                      label={`Print Selected (${selectedPartIds.size})`}
                      isSubmit={false}
                      onClick={() => handleSelectedPartsPrint()}
                      classes="!bg-blue-600 hover:!bg-blue-700"
                    />
                    <ButtonInput
                      label={`Mark Selected Removed (${selectedPartIds.size})`}
                      isSubmit={false}
                      onClick={() => handleMarkSelectedAsRemoved()}
                      classes="!bg-orange-600 hover:!bg-orange-700"
                    />
                  </>
                )}
                {job?.startDate && job?.endDate && (
                  <ButtonInput
                    label="Reset Job"
                    isSubmit={false}
                    onClick={() => handleReset()}
                    classes=""
                  />
                )}
                {job && (
                  <button
                    type="button"
                    onClick={() => {
                      setJobLocked(job.id, !locked);
                    }}
                    className={classNames(
                      "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition",
                      locked
                        ? "bg-slate-800 text-white hover:bg-slate-700"
                        : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                    )}
                    title={
                      locked
                        ? "Unlock job to allow reassignment"
                        : "Lock job to prevent reassignment"
                    }
                  >
                    {locked ? (
                      <>
                        <LockOpenIcon className="h-4 w-4" aria-hidden="true" />
                        Unlock
                      </>
                    ) : (
                      <>
                        <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
                        Lock
                      </>
                    )}
                  </button>
                )}
                {completed ? (
                  <ButtonInput
                    label="Reopen Job"
                    isSubmit={false}
                    onClick={() => handleReopen()}
                    classes="!bg-green-600 hover:!bg-green-700"
                  />
                ) : (
                  <ButtonInput
                    label="Complete Job"
                    isSubmit={false}
                    onClick={() => setCompleteOpen(true)}
                    classes="!bg-green-600 hover:!bg-green-700"
                  />
                )}
                {job?.lead?.vehicle?.id && (
                  <ButtonInput
                    label="View Vehicle"
                    isSubmit={false}
                    onClick={() =>
                      navigate(`/vehicles/${job.lead!.vehicle!.id}`)
                    }
                    classes=""
                  />
                )}
              </>
            }
          />
        )}

        {/* Vehicle workflow stepper — identical component used on the Vehicle
            page so the lifecycle status (Collected → Checked-in → Depolluted →
            Dismantled) reads the same way everywhere. Scroll target for the
            "Create Jobs" action is the parts card id below. */}
        {job?.lead?.vehicle && (
          <VehicleWorkflowCard
            vehicle={job.lead.vehicle}
            createJobsTargetId={`job-parts-${job.id}`}
          />
        )}

        {/* Dismantling Summary — the header specifically for this job (separate from
            the vehicle card above). Title, description, assignee, estimated
            dismantling time, parts-to-remove progress, and lifecycle chip. */}
        <Card
          title="Dismantling Summary"
          bodyClassName="p-4 sm:p-5"
          headerRight={
            job?.lead?.vehicle?.umc ? (
              <ButtonInput
                label="View parts diagram"
                isSubmit={false}
                onClick={handleOpenVehiclePartsDiagram}
                disabled={
                  !job?.jobPartCategories?.some((j) => j.partCategory)
                }
                classes="!text-sm"
                size="sm"
              />
            ) : undefined
          }
        >
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Title + description — primary column, widest so long notes have
                room without wrapping awkwardly. */}
            <div className="lg:col-span-2">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-semibold text-slate-900">
                  {job?.title?.replace(/-[^-]*$/, "") ?? "Job"}
                </h2>
                {completed ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                    <CheckCircleIcon
                      className="h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                    Completed
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
                    <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    In progress
                  </span>
                )}
                {locked && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2 py-0.5 text-xs font-semibold text-white ring-1 ring-inset ring-slate-900">
                    <LockClosedIcon
                      className="h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                    Locked
                  </span>
                )}
              </div>

              {job?.description && (
                <p className="mt-3 whitespace-pre-wrap text-sm text-slate-600">
                  {job.description}
                </p>
              )}

              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Assigned to
                  </dt>
                  <dd className="mt-0.5 flex items-center gap-1 text-slate-700">
                    <UserCircleIcon
                      className="h-4 w-4 text-slate-400"
                      aria-hidden="true"
                    />
                    {assignedMechanic ?? "Unassigned"}
                  </dd>
                </div>
                {job?.created && (
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      Created
                    </dt>
                    <dd className="mt-0.5 text-slate-700">
                      {formatDateTime(job.created, DateTime.DATE_MED)}
                    </dd>
                  </div>
                )}
                {job?.startDate && (
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      Scheduled
                    </dt>
                    <dd className="mt-0.5 text-slate-700">
                      {formatDateTime(job.startDate, DateTime.DATETIME_MED)}
                    </dd>
                  </div>
                )}
                {job?.completedOn && (
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      Completed
                    </dt>
                    <dd className="mt-0.5 text-slate-700">
                      {formatDateTime(job.completedOn, DateTime.DATETIME_MED)}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* KPI column — estimated time + parts progress. Same visual
                language (big number, muted label) as other Wilma stat cards
                so it reads quickly. */}
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                <p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <ClockIcon
                    className="h-3.5 w-3.5 text-slate-400"
                    aria-hidden="true"
                  />
                  Estimated dismantling
                </p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">
                  {formattedDuration}
                </p>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                <p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <WrenchScrewdriverIcon
                    className="h-3.5 w-3.5 text-slate-400"
                    aria-hidden="true"
                  />
                  Parts to remove
                </p>
                <p className="mt-1 flex items-baseline gap-2">
                  <span className="text-2xl font-semibold text-slate-900">
                    {jobSummary.remaining}
                  </span>
                  <span className="text-sm text-slate-500">
                    of {jobSummary.total}
                  </span>
                </p>

                {/* Progress bar — only meaningful when there are parts. */}
                {jobSummary.total > 0 && (
                  <>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                      <div
                        className="h-full rounded-full bg-emerald-500 transition-all"
                        style={{ width: `${jobSummary.progress}%` }}
                      />
                    </div>
                    <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
                      <span className="text-emerald-700">
                        {jobSummary.removed} removed
                      </span>
                      {jobSummary.damaged > 0 && (
                        <span className="text-rose-600">
                          {jobSummary.damaged} damaged
                        </span>
                      )}
                      <span>{jobSummary.progress}%</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Dismantling stopwatch — lets a mechanic start/pause/stop against
            this job so we capture actual hands-on time (as opposed to
            `job.duration`, which is the estimate set at scheduling). */}
        {job?.id && (
          <DismantlingTimerCard
            jobId={job.id}
            userId={userId ?? null}
            estimatedMinutes={job.duration ?? null}
            onFirstStart={handleTimerFirstStart}
            onStop={handleTimerStop}
            onReset={handleTimerReset}
          />
        )}

        <Card title="Job Notes" bodyClassName="px-2 pb-4">
          <NotesOutput
            notes={job?.notes!}
            resourceType={ResourceType.Job}
            resourceId={job?.id!}
            refetchQuery={["job", jobId]}
            shouldScroll={false}
          />
        </Card>

        {job?.lead?.vehicle?.id && (
          <Depollution vehicleId={job.lead.vehicle.id} collapsible />
        )}

        <Card
          title="Parts"
          bodyClassName="p-4"
          headerRight={
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPartProOpen(true)}
                disabled={
                  partProLoading || partProBlacklisted || partProUnavailable
                }
                title={
                  partProBlacklisted
                    ? "PartPro lookups aren't supported for this manufacturer"
                    : partProUnavailable
                      ? "This job has no linked lead or vehicle — can't run PartPro"
                      : partProHasLookup
                        ? "Refresh PartPro data for this vehicle"
                        : "Run a PartPro lookup on this vehicle"
                }
                className={classNames(
                  "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm font-semibold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-wilma-500/40",
                  partProLoading || partProBlacklisted || partProUnavailable
                    ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                    : partProHasLookup
                      ? "border-wilma-200 bg-white text-wilma-700 hover:border-wilma-300 hover:bg-wilma-50"
                      : "border-transparent bg-wilma-500 text-white hover:bg-wilma-600"
                )}
              >
                <KeyIcon className="h-4 w-4" aria-hidden="true" />
                {partProLoading
                  ? "Looking up…"
                  : partProHasLookup
                    ? "Refresh PartPro"
                    : "PartPro Lookup"}
              </button>
              <div
                className="flex items-center gap-0.5 rounded-lg bg-slate-100 p-0.5 ring-1 ring-slate-200/80"
                role="group"
                aria-label="Parts layout"
              >
                <button
                  type="button"
                  aria-pressed={jobPartsView === "tiles"}
                  aria-label="Tiles view"
                  onClick={() => setJobPartsView("tiles")}
                  className={classNames(
                    "rounded-md p-1.5 transition",
                    jobPartsView === "tiles"
                      ? "bg-white text-primary-700 shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  )}
                >
                  <Squares2X2Icon className="h-5 w-5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-pressed={jobPartsView === "table"}
                  aria-label="Table view"
                  onClick={() => setJobPartsView("table")}
                  className={classNames(
                    "rounded-md p-1.5 transition",
                    jobPartsView === "table"
                      ? "bg-white text-primary-700 shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  )}
                >
                  <TableCellsIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          }
        >
          <div id={`job-parts-${job?.id}`}>
            <JobPartsByAssembly
              viewMode={jobPartsView}
              jobPartCategories={
                showDeleted
                  ? (job?.jobPartCategories || [])
                  : (job?.jobPartCategories || []).filter((jpc) => {
                    if (!jpc.partData) return true;
                    const part = jpc.partData as IPart & {
                      deleted?: string | null;
                    };
                    return !part.deleted;
                  })
              }
              markAsRemoved={markAsRemoved}
              setDamaged={setDamaged}
              setDamagedOpen={setDamagedOpen}
              handlePrint={handlePrint}
              selectedPartIds={selectedPartIds}
              togglePartSelection={togglePartSelection}
              toggleAssemblySelection={toggleAssemblySelection}
              onDiagramClick={handleDiagramClick}
            />
          </div>
        </Card>

        <div className="flex justify-end">
          <ButtonInput
            label="Delete Job"
            isSubmit={false}
            onClick={() => setDeleteOpen(true)}
            classes="!bg-red-600 hover:!bg-red-700"
          />
        </div>
      </div>
      <iframe id="frame" className="h-0 w-0 absolute"></iframe>
    </>
  )
}

